import React, { useRef, useState, useEffect } from "react";
import {
  upload_image_message_thunk,
  upload_audio_message_thunk,
} from "../../redux/chat/chatThunk";
import { useDispatch } from "react-redux";

const MessageInput = ({
  message,
  setMessage,
  handleSendMessage,
  loading,
  messageInputRef,
  user,
  activeChat,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState(null);
  const [showAudioPreview, setShowAudioPreview] = useState(false);

  // Refs
  const fileInputRef = useRef(null);
  const previewContainerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioPlayerRef = useRef(null);

  const dispatch = useDispatch();

  // Focus the preview container when entering preview mode or when an image is pasted
  useEffect(() => {
    if (previewMode && previewImage && previewContainerRef.current) {
      setTimeout(() => {
        previewContainerRef.current.focus();
      }, 50);
    }
  }, [previewMode, previewImage]);

  // Format recording time (seconds to MM:SS)
  const formatRecordingTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Start audio recording
  const startRecording = async () => {
    try {
      // Get user media (microphone)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Reset audio chunks
      audioChunksRef.current = [];

      // Create media recorder
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      // Handle data available event
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      // Handle recording stop event
      mediaRecorder.onstop = () => {
        // Create audio blob
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        setAudioBlob(audioBlob);

        // Create audio URL for preview
        const audioURL = URL.createObjectURL(audioBlob);
        setAudioURL(audioURL);

        // Show audio preview
        setShowAudioPreview(true);

        // Stop all tracks in the stream
        stream.getTracks().forEach((track) => track.stop());
      };

      // Start recording
      mediaRecorder.start();
      setIsRecording(true);

      // Start timer for recording duration
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting audio recording:", error);
      alert(
        "Failed to access microphone. Please check your browser permissions."
      );
    }
  };

  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      // Clear recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  // Cancel audio recording
  const cancelRecording = () => {
    // Stop recording if in progress
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      // Clear recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }

    // Clear audio preview
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
    }

    // Reset audio state
    setAudioBlob(null);
    setAudioURL(null);
    setShowAudioPreview(false);
    setRecordingTime(0);
  };

  // Send audio message
  const sendAudioMessage = () => {
    if (!audioBlob || !activeChat || !user?._id) return;

    setIsUploading(true);

    // Create form data
    const formData = new FormData();

    // Create a file from the blob with a specific name and extension
    const audioFile = new File(
      [audioBlob],
      `audio_message_${Date.now()}.webm`,
      { type: "audio/webm" }
    );

    formData.append("audio", audioFile);
    formData.append("chatRoomId", activeChat);
    formData.append("sender", user._id);
    formData.append(
      "senderName",
      `${user.firstName} ${user.lastName}`.trim() || "User"
    );
    formData.append("message", message.trim() || "Audio message"); // Use current message text as caption or default
    formData.append("duration", recordingTime.toString());

    // Reset states
    setMessage("");
    setAudioBlob(null);
    setAudioURL(null);
    setShowAudioPreview(false);
    setRecordingTime(0);

    // Upload the audio
    dispatch(upload_audio_message_thunk(formData))
      .unwrap()
      .then((result) => {})
      .catch((error) => {
        console.error("Error uploading audio:", error);
        alert("Failed to upload audio. Please try again.");
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const triggerFileUpload = () => {
    fileInputRef.current.click();
  };

  // Toggle preview mode
  const togglePreviewMode = () => {
    setPreviewMode((prev) => !prev);

    // If switching to edit mode, keep the preview image
    // If switching away from preview mode (canceling), clear the preview image
    if (previewMode) {
      // Keep the image when going back to edit
    } else if (!previewMode && !message.trim() && previewImage) {
      // If there's no text but we're toggling preview off, clear the image preview
      setPreviewImage(null);
    }
  };

  // Handle key down for Shift+Enter and Tab
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (showAudioPreview) {
        sendAudioMessage();
      } else if (previewMode && previewImage) {
        sendImageMessage(e);
      } else {
        handleSendMessage(e);
      }
    } else if (e.key === "Enter" && e.shiftKey) {
      // Handle Shift+Enter for lists
      const textarea = e.target;
      const start = textarea.selectionStart;
      const value = textarea.value;

      // Get the current line
      const beforeCursor = value.substring(0, start);
      const afterCursor = value.substring(start);
      const currentLineStart = beforeCursor.lastIndexOf("\n") + 1;
      const currentLine = beforeCursor.substring(currentLineStart);

      // Check if we're in a list item (numbered, alphabetical, or Roman)
      const numListMatch = currentLine.match(/^(\s*)(\d+)\.(\s.*)$/);
      const alphaListMatch = currentLine.match(/^(\s*)([a-z])\.(\s.*)$/);
      const romanListMatch = currentLine.match(/^(\s*)([ivxlcdm]+)\.(\s.*)$/i);

      if (numListMatch) {
        // We're in a numbered list
        e.preventDefault();
        const spaces = numListMatch[1];
        const number = parseInt(numListMatch[2]);
        const nextNumber = number + 1;

        // Create a new line with the next number
        const newLine = `\n${spaces}${nextNumber}. `;

        // Insert the new line
        const newValue = value.substring(0, start) + newLine + afterCursor;

        // Update textarea value
        setMessage(newValue);

        // Set cursor position after the inserted newline
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd =
            start + newLine.length;
        }, 0);
      } else if (alphaListMatch) {
        // We're in an alphabetical list
        e.preventDefault();
        const spaces = alphaListMatch[1];
        const letter = alphaListMatch[2];
        const nextLetterCode = letter.charCodeAt(0) + 1;

        // Only proceed if we're still within a-z range
        if (nextLetterCode <= 122) {
          // 'z' is 122
          const nextLetter = String.fromCharCode(nextLetterCode);

          // Create a new line with the next letter
          const newLine = `\n${spaces}${nextLetter}. `;

          // Insert the new line
          const newValue = value.substring(0, start) + newLine + afterCursor;

          // Update textarea value
          setMessage(newValue);

          // Set cursor position after the inserted newline
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd =
              start + newLine.length;
          }, 0);
        } else {
          // If we've reached 'z', just add a regular newline
          const newValue = value.substring(0, start) + "\n" + afterCursor;

          setMessage(newValue);

          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = start + 1;
          }, 0);
        }
      } else if (romanListMatch) {
        // We're in a Roman numeral list
        e.preventDefault();
        const spaces = romanListMatch[1];
        const roman = romanListMatch[2].toLowerCase();

        // Roman numeral conversion (simple for i through x)
        const romanNumerals = [
          "i",
          "ii",
          "iii",
          "iv",
          "v",
          "vi",
          "vii",
          "viii",
          "ix",
          "x",
        ];
        const currentIndex = romanNumerals.indexOf(roman);

        if (currentIndex >= 0 && currentIndex < romanNumerals.length - 1) {
          const nextRoman = romanNumerals[currentIndex + 1];

          // Create a new line with the next Roman numeral
          const newLine = `\n${spaces}${nextRoman}. `;

          // Insert the new line
          const newValue = value.substring(0, start) + newLine + afterCursor;

          // Update textarea value
          setMessage(newValue);

          // Set cursor position after the inserted newline
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd =
              start + newLine.length;
          }, 0);
        } else {
          // If we can't determine the next Roman numeral, just add a regular newline
          const newValue = value.substring(0, start) + "\n" + afterCursor;

          setMessage(newValue);

          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = start + 1;
          }, 0);
        }
      }
      // If not in a list item, let the default Shift+Enter behavior happen
      // No need to preventDefault() here
    }
    // Handle Tab key for indentation
    else if (e.key === "Tab") {
      e.preventDefault();
      handleTabKey(e);
    }
    // For other keys, allow default behavior
  };

  // Handle Tab key for indentation
  const handleTabKey = (e) => {
    const textarea = e.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;

    // Get the current line
    const beforeCursor = value.substring(0, start);
    const afterCursor = value.substring(end);
    const currentLineStart = beforeCursor.lastIndexOf("\n") + 1;
    const currentLine = beforeCursor.substring(currentLineStart);

    // Check if we're in a numbered list item or an alphabetical list item
    const numListMatch = currentLine.match(/^(\s*)(\d+)\.(\s.*)$/);
    const alphaListMatch = currentLine.match(/^(\s*)([a-z])\.(\s.*)$/);

    if (numListMatch) {
      // We're in a numbered list item
      const spaces = numListMatch[1];
      const content = numListMatch[3];

      // Add one level of indentation (4 spaces)
      const newIndent = spaces + "    ";

      // Always start with 'a' for first level indentation from numbered list
      const newListItem = `${newIndent}a.${content}`;

      // Replace the current line with the indented and converted version
      const newValue =
        value.substring(0, currentLineStart) + newListItem + afterCursor;

      // Update textarea value
      setMessage(newValue);

      // Set cursor position
      const newCursorPos =
        currentLineStart + newListItem.length - afterCursor.length;
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = newCursorPos;
      }, 0);
    } else if (alphaListMatch) {
      // We're in an alphabetical list item
      const spaces = alphaListMatch[1];
      const content = alphaListMatch[3];

      // Add one level of indentation (4 spaces)
      const newIndent = spaces + "    ";

      // Convert to Roman numerals (always start with i)
      const newListItem = `${newIndent}i.${content}`;

      // Replace the current line with the indented and converted version
      const newValue =
        value.substring(0, currentLineStart) + newListItem + afterCursor;

      // Update textarea value
      setMessage(newValue);

      // Set cursor position
      const newCursorPos =
        currentLineStart + newListItem.length - afterCursor.length;
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = newCursorPos;
      }, 0);
    } else {
      // Regular tab behavior - insert 4 spaces
      const newValue =
        value.substring(0, start) + "    " + value.substring(end);

      // Update textarea value
      setMessage(newValue);

      // Set cursor position after the inserted tab
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 4;
      }, 0);
    }
  };

  // Convert text to HTML with line breaks and preserved indentation
  const formatPreviewText = (text) => {
    if (!text) return "";

    // The styles should already be applied in the input text
    // Just split by lines and preserve formatting
    return text.split("\n").map((line, i) => (
      <React.Fragment key={i}>
        <span
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            display: "inline-block",
            width: "100%",
            textAlign: "left",
            paddingLeft: "0",
          }}
        >
          {line}
        </span>
        {i < text.split("\n").length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Handle direct image paste
  const handlePaste = (e) => {
    if (!activeChat || !user?._id) return;

    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        e.preventDefault();

        // Get image file from clipboard
        const file = items[i].getAsFile();
        if (!file) continue;

        // Check file size (5MB limit)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
          alert("Image size should be less than 5MB");
          return;
        }

        // Create a preview URL for the image
        const imageUrl = URL.createObjectURL(file);
        setPreviewImage({
          file: file,
          url: imageUrl,
        });

        // Switch to preview mode
        setPreviewMode(true);

        break;
      }
    }
  };

  const handleImageUpload = (e) => {
    if (!activeChat || !user?._id) return;

    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      alert("Please select a valid image file (JPEG, PNG, GIF, WebP)");
      return;
    }

    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert("Image size should be less than 5MB");
      return;
    }

    // Create a preview URL for the image
    const imageUrl = URL.createObjectURL(file);
    setPreviewImage({
      file: file,
      url: imageUrl,
    });

    // Switch to preview mode
    setPreviewMode(true);

    // Reset the file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Send image message
  const sendImageMessage = (e) => {
    if (e) e.preventDefault();
    if (!previewImage?.file || !activeChat || !user?._id) return;

    setIsUploading(true);

    // Create form data
    const formData = new FormData();
    formData.append("image", previewImage.file);
    formData.append("chatRoomId", activeChat);
    formData.append("sender", user._id);
    formData.append(
      "senderName",
      `${user.firstName} ${user.lastName}`.trim() || "User"
    );
    formData.append("message", message.trim() || "Image"); // Use current message text as caption or default

    // Reset states
    setMessage("");
    setPreviewImage(null);
    setPreviewMode(false);

    // Upload the image
    dispatch(upload_image_message_thunk(formData))
      .unwrap()
      .then((result) => {})
      .catch((error) => {
        console.error("Error uploading image:", error);
        alert("Failed to upload image. Please try again.");
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  // Handle send - either text message or image message
  const handleSend = (e) => {
    if (e) e.preventDefault();

    if (showAudioPreview) {
      sendAudioMessage();
    } else if (previewImage) {
      sendImageMessage(e);
    } else {
      handleSendMessage(e);
    }
  };

  return (
    <div className="p-3 md:p-4 bg-black bg-opacity-40 border-t border-gray-800">
      {/* Audio preview UI */}
      {showAudioPreview && (
        <div className="mb-3 p-4 bg-gray-800 rounded-lg text-white border border-gray-700">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-purple-400 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                />
              </svg>
              Voice Message Preview
            </span>
            <button
              onClick={() => {
                URL.revokeObjectURL(audioURL);
                setShowAudioPreview(false);
                setAudioBlob(null);
                setAudioURL(null);
              }}
              className="text-xs px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-full text-gray-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>

          <div className="audio-preview bg-gray-900 rounded-lg p-3 mb-3">
            <audio
              ref={audioPlayerRef}
              controls
              src={audioURL}
              className="w-full audio-player"
              preload="auto"
            >
              Your browser does not support the audio element.
            </audio>
          </div>

          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-400 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {recordingTime > 0
                ? `Duration: ${formatRecordingTime(recordingTime)}`
                : "Ready to send"}
            </div>
            <button
              onClick={sendAudioMessage}
              className="px-4 py-1.5 bg-purple-600 hover:bg-purple-500 rounded-full text-white flex items-center transition-colors text-sm"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              Send Voice
            </button>
          </div>
        </div>
      )}

      {/* Recording UI */}
      {isRecording && (
        <div className="mb-3 p-4 bg-gray-800 rounded-lg text-white border border-gray-700 relative overflow-hidden">
          <div className="flex justify-between items-center mb-2 relative z-10">
            <div className="flex items-center">
              <div className="flex items-center justify-center rounded-full bg-red-500 h-4 w-4 mr-2">
                <div className="h-2 w-2 bg-red-700 rounded-full animate-ping absolute"></div>
              </div>
              <span className="text-sm font-medium">
                Recording Your Voice...
              </span>
            </div>
            <div className="text-sm font-mono">
              {formatRecordingTime(recordingTime)}
            </div>
          </div>

          {/* Simple recording indicator */}
          <div className="recording-indicator h-16 flex items-center justify-center my-2 relative z-10">
            <div className="px-4 py-2 bg-gray-900 rounded-lg flex items-center">
              <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse mr-3"></div>
              <span className="text-sm text-gray-300">
                Microphone active - speak now
              </span>
            </div>
          </div>

          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/20 to-red-900/20 animate-pulse"></div>

          <div className="mt-3 flex justify-end space-x-3 relative z-10">
            <button
              onClick={cancelRecording}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-full text-white text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={stopRecording}
              className="px-4 py-1.5 bg-purple-600 hover:bg-purple-500 rounded-full text-white text-sm transition-colors flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
                />
              </svg>
              Stop Recording
            </button>
          </div>
        </div>
      )}

      {previewMode && (message.trim() || previewImage) && (
        <div
          ref={previewContainerRef}
          className="mb-3 p-3 bg-gray-800 rounded-lg text-white"
          onKeyDown={handleKeyDown}
          tabIndex="0"
        >
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-purple-400">
              Message Preview
            </span>
            <button
              onClick={togglePreviewMode}
              className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300"
            >
              Close
            </button>
          </div>
          {previewImage && (
            <div className="preview-image mb-2">
              <img
                src={previewImage.url}
                alt="Preview"
                className="rounded max-w-full max-h-60 object-contain"
              />
            </div>
          )}
          <div className="preview-content">{formatPreviewText(message)}</div>
          <div className="mt-2 text-xs text-gray-400">
            Press Enter to send or click the send button
          </div>
        </div>
      )}

      <form onSubmit={handleSend} className="flex space-x-2">
        {/* Audio recording button */}
        {!isRecording && !showAudioPreview && !previewMode && (
          <button
            type="button"
            onClick={startRecording}
            disabled={isUploading || loading}
            className={`text-white rounded-full p-2 hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-gray-900 shadow-lg transition-all duration-200 h-[40px] w-[40px] flex items-center justify-center flex-shrink-0 bg-gray-800 ${
              isUploading ? "opacity-50 cursor-not-allowed" : ""
            }`}
            title="Record Voice Message"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          </button>
        )}

        {/* Image upload button */}
        <button
          type="button"
          onClick={triggerFileUpload}
          disabled={
            isUploading ||
            loading ||
            previewMode ||
            isRecording ||
            showAudioPreview
          }
          className={`text-white bg-gray-800 rounded-full p-2 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-gray-900 shadow-lg transition-all duration-200 h-[40px] w-[40px] flex items-center justify-center flex-shrink-0 ${
            isUploading || previewMode || isRecording || showAudioPreview
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
          title="Upload Image"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </button>

        {/* Text input with dynamic height */}
        <div className="flex-1 relative">
          <textarea
            ref={messageInputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (Shift+Enter for new line, Tab for indentation (e.g '1.'+ space + press Tab))"
            className="w-full py-2 text-[12px] px-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono"
            disabled={isRecording || isUploading || loading}
            rows={Math.min(message.split("\n").length || 1, 20)}
            style={{
              height: `${Math.min(
                Math.max((message.split("\n").length || 1) * 20 + 20, 40),
                420
              )}px`,
              transition: "height 0.1s ease",
            }}
          ></textarea>
        </div>

        {/* Send button */}
        <button
          type="submit"
          className={`rounded-full p-2 bg-purple-600 text-white hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-gray-900 shadow-lg transition-all duration-200 h-[40px] w-[40px] flex items-center justify-center flex-shrink-0 ${
            (!message.trim() && !previewImage && !showAudioPreview) ||
            isUploading ||
            loading ||
            isRecording
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
          disabled={
            (!message.trim() && !previewImage && !showAudioPreview) ||
            isUploading ||
            loading ||
            isRecording
          }
        >
          {/* Send icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleImageUpload}
        />
      </form>
    </div>
  );
};

export default MessageInput;
